import { type FC, useEffect, useState } from 'react';

import type {
  CarePlanViewModel,
  ScheduleSegmentVm,
  SetScheduleBody,
} from '@eluve/api-contract';
import { Button, Icon, Input, Stack, Tag, Text, cn } from '@eluve/components';

const UNIT_LABEL: Record<string, string> = { d: 'days', wk: 'wks', mo: 'mo' };

const segmentLabel = (s: ScheduleSegmentVm): string => {
  const period = s.period ?? 1;
  const unit = UNIT_LABEL[s.periodUnit] ?? s.periodUnit;
  const cadence =
    period === 1
      ? `${s.frequency}×/${unit}`
      : `${s.frequency}×/${period} ${unit}`;
  if (s.duration === null || s.durationUnit === null)
    return `${cadence} forever`;
  return `${cadence} for ${s.duration} ${UNIT_LABEL[s.durationUnit] ?? s.durationUnit}`;
};

const LADDER_WINDOW = 5;

export const ScheduleSection: FC<{
  schedule: CarePlanViewModel['schedule'];
  disabled?: boolean;
  onSetSchedule: (args: Omit<SetScheduleBody, 'appointmentId'>) => void;
}> = ({ schedule, disabled, onSetSchedule }) => {
  const [editing, setEditing] = useState(false);
  const [draftSegments, setDraftSegments] = useState<ScheduleSegmentVm[]>([]);
  const [reassessMode, setReassessMode] = useState<'visits' | 'days'>(
    schedule.reassess.afterDays !== null ? 'days' : 'visits',
  );
  const [reassessValue, setReassessValue] = useState<string>('');
  const [windowStart, setWindowStart] = useState(() => {
    const current = schedule.ladder.findIndex((s) => s.status === 'current');
    return Math.max(
      0,
      Math.min(current, schedule.ladder.length - LADDER_WINDOW),
    );
  });

  const updateSegment = (
    index: number,
    update: (segment: ScheduleSegmentVm) => ScheduleSegmentVm,
  ) =>
    setDraftSegments((segments) =>
      segments.map((segment, i) => (i === index ? update(segment) : segment)),
    );

  useEffect(() => {
    setWindowStart((start) =>
      Math.min(start, Math.max(0, schedule.ladder.length - LADDER_WINDOW)),
    );
  }, [schedule.ladder.length]);

  const startEditing = () => {
    setDraftSegments(
      schedule.segments.length > 0
        ? schedule.segments.map((s) => ({ ...s }))
        : [
            {
              frequency: 2,
              period: null,
              periodUnit: 'wk',
              duration: 2,
              durationUnit: 'wk',
            },
          ],
    );
    setReassessMode(schedule.reassess.afterDays !== null ? 'days' : 'visits');
    setReassessValue(
      String(
        schedule.reassess.afterVisits ?? schedule.reassess.afterDays ?? '',
      ),
    );
    setEditing(true);
  };

  const save = () => {
    const value = Number.parseInt(reassessValue, 10);
    const visitValue = Math.max(2, value);
    onSetSchedule({
      segments: draftSegments,
      reassess: {
        afterVisits:
          reassessMode === 'visits' && Number.isFinite(visitValue)
            ? visitValue
            : null,
        afterDays:
          reassessMode === 'days' && Number.isFinite(value) ? value : null,
      },
      reassessBooked: schedule.reassess.bookedManually,
    });
    setEditing(false);
  };

  const visibleSteps = schedule.ladder.slice(
    windowStart,
    windowStart + LADDER_WINDOW,
  );

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <Stack direction="horizontal" align="center" justify="between" gap={3}>
        <Text weight="semibold">Schedule</Text>
        {!editing && (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={startEditing}
            aria-label="Edit schedule"
          >
            <Icon name="Pencil" size="auto" />
          </Button>
        )}
      </Stack>

      {/* Summary + visit ladder stay visible whether or not the cadence
          editor is open (design keeps the ladder above the editor). */}
      <>
        <div className="mt-2">
          <Text weight="medium">
            {schedule.totalVisits !== null
              ? `${schedule.totalVisits} visits`
              : schedule.segments.length > 0
                ? 'Ongoing'
                : 'No cadence set'}
          </Text>
          {schedule.segments.length > 0 && (
            <Text size="sm" color="secondary" className="block">
              {schedule.segments.map(segmentLabel).join(' → ')}
            </Text>
          )}
        </div>

        {schedule.ladder.length > 0 && (
          <Stack direction="horizontal" align="center" gap={2} className="mt-6">
            <Button
              size="sm"
              variant="ghost"
              disabled={windowStart === 0}
              onClick={() => setWindowStart((s) => Math.max(0, s - 1))}
              aria-label="Earlier visits"
            >
              <Icon name="ChevronLeft" size="auto" />
            </Button>
            <Stack
              direction="horizontal"
              align="start"
              justify="between"
              gap={2}
              className="flex-1"
            >
              {visibleSteps.map((step, index) => (
                <div
                  key={step.number}
                  className="relative flex min-w-0 flex-1 flex-col items-center gap-1.5 pt-7"
                >
                  {index < visibleSteps.length - 1 && (
                    <span
                      aria-hidden
                      className="absolute top-[43px] left-[calc(50%+20px)] h-px w-[calc(100%-40px)] bg-border-secondary"
                    />
                  )}
                  {step.reassess && (
                    <Tag
                      size="xs"
                      color="warning"
                      label="⚑ Reassess"
                      className="absolute top-0"
                    />
                  )}
                  <span
                    title={`Visit ${step.number} · ${
                      step.status === 'current'
                        ? 'this visit'
                        : step.status === 'completed'
                          ? 'completed'
                          : 'upcoming'
                    }`}
                    aria-current={
                      step.status === 'current' ? 'step' : undefined
                    }
                    className={cn(
                      'flex size-8 items-center justify-center rounded-full border text-sm',
                      step.status === 'current' &&
                        'border-border-insights-subtle bg-bg-insights-strong-primary text-text-insights-strong',
                      step.status === 'completed' &&
                        'border-border-secondary bg-bg-secondary text-text-secondary',
                      step.status === 'upcoming' &&
                        cn(
                          'border-border-secondary text-text-secondary',
                          step.reassess &&
                            'border-border-warning-subtle bg-bg-warning-subtle',
                        ),
                      step.booked && 'font-semibold',
                    )}
                  >
                    {step.status === 'completed' ? (
                      <Icon name="Check" size="xs" />
                    ) : (
                      step.number
                    )}
                  </span>
                  <Text size="xs" color="secondary">
                    Visit {step.number}
                  </Text>
                </div>
              ))}
            </Stack>
            <Button
              size="sm"
              variant="ghost"
              disabled={windowStart + LADDER_WINDOW >= schedule.ladder.length}
              onClick={() =>
                setWindowStart((s) =>
                  Math.min(s + 1, schedule.ladder.length - LADDER_WINDOW),
                )
              }
              aria-label="Later visits"
            >
              <Icon name="ChevronRight" size="auto" />
            </Button>
          </Stack>
        )}
      </>

      {editing && (
        <Stack
          direction="vertical"
          gap={3}
          className="mt-4 border-t border-border-secondary pt-3"
        >
          <Text
            size="xs"
            weight="semibold"
            color="secondary"
            className="tracking-wide uppercase"
          >
            Cadence
          </Text>
          {draftSegments.map((segment, i) => (
            <Stack key={i} direction="horizontal" align="center" gap={2} wrap>
              <Input
                type="number"
                min={1}
                value={segment.frequency}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) => ({
                    ...s,
                    frequency: Number(e.target.value) || 1,
                  }))
                }
                className="w-16"
                aria-label={`Segment ${i + 1} frequency`}
              />
              <Text size="sm">×/</Text>
              <select
                value={segment.periodUnit}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) => ({
                    ...s,
                    periodUnit: e.target
                      .value as ScheduleSegmentVm['periodUnit'],
                  }))
                }
                className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
                aria-label={`Segment ${i + 1} period unit`}
              >
                <option value="wk">per week</option>
                <option value="mo">per month</option>
              </select>
              <Text size="sm">for</Text>
              <Input
                type="number"
                min={1}
                value={segment.duration ?? ''}
                placeholder="∞"
                disabled={disabled}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  updateSegment(i, (s) =>
                    Number.isFinite(n)
                      ? {
                          ...s,
                          duration: n,
                          durationUnit: s.durationUnit ?? s.periodUnit,
                        }
                      : { ...s, duration: null, durationUnit: null },
                  );
                }}
                className="w-16"
                aria-label={`Segment ${i + 1} duration`}
              />
              <select
                value={segment.durationUnit ?? 'forever'}
                disabled={disabled}
                onChange={(e) =>
                  updateSegment(i, (s) =>
                    e.target.value === 'forever'
                      ? { ...s, duration: null, durationUnit: null }
                      : {
                          ...s,
                          durationUnit: e.target.value as NonNullable<
                            ScheduleSegmentVm['durationUnit']
                          >,
                          duration: s.duration ?? 1,
                        },
                  )
                }
                className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
                aria-label={`Segment ${i + 1} duration unit`}
              >
                <option value="wk">weeks</option>
                <option value="mo">months</option>
                <option value="d">days</option>
                <option value="forever">forever</option>
              </select>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled || draftSegments.length === 1}
                onClick={() =>
                  setDraftSegments((prev) => prev.filter((_, j) => j !== i))
                }
                aria-label={`Remove segment ${i + 1}`}
              >
                <Icon name="Trash2" size="auto" />
              </Button>
            </Stack>
          ))}
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() =>
              setDraftSegments((prev) => [
                ...prev,
                {
                  frequency: 1,
                  period: null,
                  periodUnit: 'wk',
                  duration: null,
                  durationUnit: null,
                },
              ])
            }
            className="self-start"
          >
            <Icon name="Plus" size="auto" />
            Add Cadence
          </Button>

          <Stack
            direction="horizontal"
            align="center"
            gap={2}
            wrap
            className="border-t border-border-secondary pt-3"
          >
            <Text size="sm">Reassess after</Text>
            <Input
              type="number"
              min={reassessMode === 'visits' ? 2 : 1}
              value={reassessValue}
              disabled={disabled}
              onChange={(e) => setReassessValue(e.target.value)}
              className="w-16"
              aria-label="Reassess after"
            />
            <select
              value={reassessMode}
              disabled={disabled}
              onChange={(e) =>
                setReassessMode(e.target.value as 'visits' | 'days')
              }
              className="rounded-md border border-border-secondary bg-bg-primary px-2 py-1.5 text-sm"
              aria-label="Reassess unit"
            >
              <option value="visits">visits</option>
              <option value="days">days</option>
            </select>
          </Stack>

          <Stack
            direction="horizontal"
            gap={2}
            justify="end"
            className="border-t border-border-secondary pt-3"
          >
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={disabled} onClick={save}>
              Save Changes
            </Button>
          </Stack>
        </Stack>
      )}
    </section>
  );
};
